import { clients } from '@/lib/https';
import type { UserRole } from '@/state/store/auth/index.type';
import type {
  IamUser,
  IamGetUsersResponse,
  IamGetUserResponse,
  IamMembership,
  AdminUser,
  InviteAdminUserInput,
} from '../types/admin-users.types';

const projectKey = import.meta.env.VITE_X_BLOCKS_KEY ?? '';
const SYSTEM_ADMIN_ORG_ID = import.meta.env.VITE_SYSTEM_ADMIN_ORG_ID ?? '';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function extractPlatformRole(user: IamUser): UserRole | null {
  const membership = user.memberships?.find((m) => m.organizationId === SYSTEM_ADMIN_ORG_ID);
  const role = membership?.roles?.[0];
  if (role === 'sys_admin' || role === 'sys_editor') return role;
  return null;
}

function toAdminUser(user: IamUser): AdminUser {
  return {
    id: user.itemId,
    email: user.email,
    firstName: user.firstName || null,
    lastName: user.lastName || null,
    role: extractPlatformRole(user),
    lastSignInAt: user.lastLoggedInTime ? new Date(user.lastLoggedInTime).getTime() : null,
    createdAt: new Date(user.createdDate).getTime(),
    imageUrl: user.profileImageUrl || null,
  };
}

async function fetchBlocksUser(userId: string): Promise<IamUser | null> {
  try {
    const result = await clients.get<IamGetUserResponse>(
      `/idp/v1/Iam/GetUser?id=${encodeURIComponent(userId)}&ProjectKey=${encodeURIComponent(projectKey)}`
    );
    return result.data || null;
  } catch {
    return null;
  }
}

async function findBlocksUserByEmail(email: string): Promise<IamUser | null> {
  try {
    const result = await clients.post<IamGetUsersResponse>(
      '/idp/v1/Iam/GetUsers',
      JSON.stringify({
        page: 0,
        pageSize: 1,
        sort: { property: 'createdDate', isDescending: true },
        filter: { email, name: '' },
        projectKey,
      })
    );
    return result.data?.[0] || null;
  } catch {
    return null;
  }
}

async function saveRolesAndPermissions(
  userId: string,
  memberships: IamMembership[]
): Promise<void> {
  await clients.post<unknown>(
    '/idp/v1/Iam/SaveRolesAndPermissions',
    JSON.stringify({
      userId,
      memberships,
      projectKey,
    })
  );
}

// ---------------------------------------------------------------------------
// Get admin users
// ---------------------------------------------------------------------------

export async function getAdminUsers(
  page = 0,
  pageSize = 50,
  search = ''
): Promise<{ users: AdminUser[]; totalCount: number }> {
  try {
    const response = await clients.post<IamGetUsersResponse>(
      '/idp/v1/Iam/GetUsers',
      JSON.stringify({
        page,
        pageSize,
        sort: { property: 'createdDate', isDescending: true },
        status: { active: true },
        filter: {
          organizationId: SYSTEM_ADMIN_ORG_ID,
          email: search,
          name: '',
        },
        projectKey,
      })
    );

    const users = (response.data || []).filter((u) => u.active).map(toAdminUser);

    return { users, totalCount: response.totalCount };
  } catch (error) {
    console.error('Error fetching admin users:', error);
    return { users: [], totalCount: 0 };
  }
}

// ---------------------------------------------------------------------------
// Update role
// ---------------------------------------------------------------------------

export async function updateAdminUserRole(
  userId: string,
  role: UserRole
): Promise<{ success: boolean }> {
  const user = await fetchBlocksUser(userId);
  if (!user) throw new Error('User not found');

  const currentMembership = user.memberships?.find((m) => m.organizationId === SYSTEM_ADMIN_ORG_ID);
  if (!currentMembership) throw new Error('User is not a system admin member');

  // Update only the System Admins org membership, keep all others intact
  const updatedMemberships: IamMembership[] = user.memberships.map((m) => {
    if (m.organizationId === SYSTEM_ADMIN_ORG_ID) {
      return {
        organizationId: m.organizationId,
        roles: [role],
        permissions: m.permissions || [],
      };
    }
    return {
      organizationId: m.organizationId,
      roles: m.roles,
      permissions: m.permissions || [],
    };
  });

  await saveRolesAndPermissions(userId, updatedMemberships);
  return { success: true };
}

// ---------------------------------------------------------------------------
// Remove user (from System Admins org)
// ---------------------------------------------------------------------------

export async function removeAdminUser(userId: string): Promise<{ success: boolean }> {
  const user = await fetchBlocksUser(userId);
  if (!user) throw new Error('User not found');

  // Remove the System Admins org membership, keep all others
  const remainingMemberships: IamMembership[] = user.memberships
    .filter((m) => m.organizationId !== SYSTEM_ADMIN_ORG_ID)
    .map((m) => ({
      organizationId: m.organizationId,
      roles: m.roles,
      permissions: m.permissions || [],
    }));

  if (remainingMemberships.length === 0) {
    remainingMemberships.push({
      organizationId: 'default',
      roles: ['user'],
      permissions: [],
    });
  }

  await saveRolesAndPermissions(userId, remainingMemberships);
  return { success: true };
}

// ---------------------------------------------------------------------------
// Invite user
// ---------------------------------------------------------------------------

export async function inviteAdminUser(data: InviteAdminUserInput): Promise<{ success: boolean }> {
  // Check if user already exists
  const existingUser = await findBlocksUserByEmail(data.email);

  if (existingUser) {
    // Check if already in System Admins org
    const existingMembership = existingUser.memberships?.find(
      (m) => m.organizationId === SYSTEM_ADMIN_ORG_ID
    );

    if (existingMembership) {
      throw new Error('A user with this email already exists in the admin org');
    }

    // User exists but NOT in admin org — add membership via SaveRolesAndPermissions
    const updatedMemberships: IamMembership[] = [
      ...(existingUser.memberships || []).map((m) => ({
        organizationId: m.organizationId,
        roles: m.roles,
        permissions: m.permissions || [],
      })),
      {
        organizationId: SYSTEM_ADMIN_ORG_ID,
        roles: [data.role],
        permissions: [],
      },
    ];

    await saveRolesAndPermissions(existingUser.itemId, updatedMemberships);
    return { success: true };
  }

  // Brand new user — create via Iam/Create
  await clients.post<unknown>(
    '/idp/v1/Iam/Create',
    JSON.stringify({
      email: data.email,
      firstName: data.firstName,
      lastName: data.lastName,
      userPassType: 1,
      userCreationType: 1,
      platform: 'legal_template',
      projectKey,
      organizationId: SYSTEM_ADMIN_ORG_ID,
      memberships: [
        {
          organizationId: 'default',
          roles: ['user'],
          permissions: [],
        },
        {
          organizationId: SYSTEM_ADMIN_ORG_ID,
          roles: [data.role],
          permissions: [],
        },
      ],
    })
  );

  return { success: true };
}
